import React, { useMemo, useState } from 'react';
import { Table, TableColumnsType, Button, Space, Modal, Tag, message } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import AddUspsAddressProcessDrawer from './components/AddUspsAddressProcessDrawer';
import {
  GET_USPS_ADDRESS_PROCESS_LIST,
  DELETE_USPS_ADDRESS_PROCESS,
  BATCH_DELETE_USPS_ADDRESS_PROCESS,
  GetUspsAddressProcessListResponse,
  UspsAddressProcessType,
  UspsProcessStatus,
} from './graphql/uspsAddressProcessGraphql';

const STATUS_COLOR_MAP: Record<UspsProcessStatus, string> = {
  PENDING: 'default',
  SUCCESS: 'green',
  FAILED: 'red',
};

const UspsAddressProcessPage: React.FC = () => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const { t } = useTranslation();

  const orderAddressIdVar = useMemo(() => {
    if (!appliedFilters.orderAddressId) return undefined;
    const n = Number(appliedFilters.orderAddressId);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [appliedFilters.orderAddressId]);

  const { data, loading, refetch } = useQuery<GetUspsAddressProcessListResponse>(
    GET_USPS_ADDRESS_PROCESS_LIST,
    {
      variables: {
        processStatus: appliedFilters.processStatus,
        orderAddressId: orderAddressIdVar,
        originalAddress: appliedFilters.originalAddress || undefined,
        createdAtFrom: appliedFilters.createdRange?.[0]?.toISOString(),
        createdAtTo: appliedFilters.createdRange?.[1]?.toISOString(),
        page: currentPage,
        size: pageSize,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [deleteUsps] = useMutation(DELETE_USPS_ADDRESS_PROCESS, {
    onCompleted: () => refetch(),
    onError: (err) => message.error(err.message),
  });

  const [batchDeleteUsps, { loading: batchDeleting }] = useMutation(BATCH_DELETE_USPS_ADDRESS_PROCESS, {
    onError: (err) => message.error(err.message),
  });

  const handleFilter = () => {
    if (filterOptions.orderAddressId && !/^\d+$/.test(filterOptions.orderAddressId.trim())) {
      message.error(t('mapTool.uspsAddressProcess.orderAddressIdMustBeNumber') as string);
      return;
    }
    setAppliedFilters({ ...filterOptions });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterOptions(DEFAULT_FILTER_OPTIONS);
    setAppliedFilters(DEFAULT_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const showDeleteConfirm = (record: UspsAddressProcessType) => {
    Modal.confirm({
      title: t('mapTool.uspsAddressProcess.deleteTitle') as string,
      content: t('mapTool.uspsAddressProcess.deleteContent') as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: () => deleteUsps({ variables: { id: Number(record.id) } }),
    });
  };

  const handleBatchDelete = () => {
    if (selectedIds.length === 0) {
      message.warning(t('mapTool.uspsAddressProcess.batchDelete.emptySelection') as string);
      return;
    }
    const ids = selectedIds.map((k) => Number(k));
    Modal.confirm({
      title: t('mapTool.uspsAddressProcess.batchDelete.title') as string,
      content: t('mapTool.uspsAddressProcess.batchDelete.confirmContent', { count: ids.length }) as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: async () => {
        try {
          const { data: resp } = await batchDeleteUsps({ variables: { ids } });
          const deleted = resp?.batchDeleteUspsAddressProcess ?? 0;
          message.success(
            t('mapTool.uspsAddressProcess.batchDelete.successMsg', {
              requested: ids.length,
              deleted,
            }) as string
          );
          setSelectedIds([]);
          refetch();
        } catch (err) {
          message.error((err as Error).message);
        }
      },
    });
  };

  const columns: TableColumnsType<UspsAddressProcessType> = [
    {
      title: t('mapTool.uspsAddressProcess.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.uspsAddressProcess.table.orderAddressId'),
      dataIndex: 'orderAddressId',
      key: 'orderAddressId',
      width: 130,
      align: 'center',
    },
    {
      title: t('mapTool.uspsAddressProcess.table.originalAddress'),
      dataIndex: 'originalAddress',
      key: 'originalAddress',
      width: 340,
      ellipsis: true,
    },
    {
      title: t('mapTool.uspsAddressProcess.table.uspsAddress'),
      dataIndex: 'uspsAddress',
      key: 'uspsAddress',
      width: 340,
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.uspsAddressProcess.table.processStatus'),
      dataIndex: 'processStatus',
      key: 'processStatus',
      width: 120,
      align: 'center',
      render: (v: UspsProcessStatus) => (
        <Tag color={STATUS_COLOR_MAP[v] ?? 'default'}>{t(`enum.uspsProcessStatus.${v}`)}</Tag>
      ),
    },
    {
      title: t('mapTool.uspsAddressProcess.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center',
      render: (v: string | null | undefined) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—'),
    },
    {
      title: t('mapTool.uspsAddressProcess.table.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      align: 'center',
      render: (v: string | null | undefined) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—'),
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 120,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) =>
        record.processStatus === 'PENDING' ? (
          <Space size="middle">
            <Button type="link" size="small" danger onClick={() => showDeleteConfirm(record)}>
              {t('components.button.delete')}
            </Button>
          </Space>
        ) : (
          '—'
        ),
    },
  ];

  const pageData = data?.uspsAddressProcessList;

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">{t('mapTool.uspsAddressProcess.pageTitle')}</h4>

      <FilterArea filters={filterOptions} setFilters={setFilterOptions} />

      <div className="flex justify-end items-center mb-4 mt-4">
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            onClick={() => setAddDrawerVisible(true)}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.new')}</span>
          </Button>
          <Button
            danger
            loading={batchDeleting}
            onClick={handleBatchDelete}
            disabled={selectedIds.length === 0}
            className="w-auto min-w-24"
          >
            <span className="font-bold">
              {t('mapTool.uspsAddressProcess.batchDelete.button')}
              {selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
            </span>
          </Button>
          <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button onClick={handleResetFilters} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: setSelectedIds,
            getCheckboxProps: (record) => ({
              disabled: record.processStatus !== 'PENDING',
            }),
          }}
          pagination={{
            current: currentPage,
            pageSize,
            total: pageData?.totalElements || 0,
            showSizeChanger: true,
            showTotal: (total) =>
              t('mapTool.uspsAddressProcess.table.paginationTotal', { total }),
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
        />
      </div>

      <AddUspsAddressProcessDrawer
        visible={addDrawerVisible}
        onClose={() => setAddDrawerVisible(false)}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default UspsAddressProcessPage;
